import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { database } from '../firebase';
import { ref, get, set, update } from 'firebase/database';
import { burst } from '../lib/confetti';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 440,
    maxHeight: '88vh',
    overflowY: 'auto',
    bgcolor: 'var(--surface)',
    boxShadow: 'var(--soft-shadow-lg)',
    p: '24px 22px',
    outline: 'none',
};

const createPlayerCombinationKey = (players) => {
    return players.map(player => player.id).sort().join('_');
}

export const calculateGameStats = async (winningPlayer, stats, players, playerWords, gameId) => {
    const newStats = {...stats};
    //const newStats = {};
    if(newStats && newStats['gameStats']) {
        return newStats;
    }

    // Calculate stats for this game
    let longestWord = '';
    let longestWordPlayer = '';
    let mostWords = 0;
    let mostWordsPlayer = '';
    for (let player of players) {
        if(playerWords[player.id]) {
            let wordCount = playerWords[player.id].length;
            if (wordCount > mostWords) {
                mostWords = wordCount;
                mostWordsPlayer = player.name;
            }
            for (let word of playerWords[player.id]) {
                if(word.length > longestWord.length) {
                    longestWord = word;
                    longestWordPlayer = player.name;
                }
            }
        }
        
            
        newStats['gameStats'] = {'winner':{'points':winningPlayer.score, 'playerName':winningPlayer.name},
                                'longestWord':{'word':longestWord,'playerName':longestWordPlayer},
                                'mostWords':{'count':mostWords,'playerName':mostWordsPlayer}
        }
    }
        
    // Calculate stats for overall combination of players
    const playerCombinationKey = createPlayerCombinationKey(players);
    let recordData = {};

    const recordRef = ref(database, `records/${playerCombinationKey}`);

            
    try {
        const snapshot = await get(recordRef);
        recordData = snapshot.val();
        
        if (!recordData) {
            console.log("Creating new record");
            // New record (i.e. first time this combo of players is playing each other)
            const wins = players.map(player => ({
                playerName: player.name,
                id: player.id,
                wins: player.id === winningPlayer.id ? 1 : 0
            }));
            recordData = {
                wins: wins,
                gameCount: 1,
                longestWord: {word: longestWord,playerName:longestWordPlayer},
                mostWords: {count: mostWords, playerName: mostWordsPlayer},
                lastGame: gameId
            }
            await set(recordRef, recordData);
        } else {
            console.log("Updating existing record");
            // Make sure we haven't already processed this game
            if (recordData.lastGame !== gameId) {
                
                recordData.gameCount += 1;

                // Update the win count and display names for the players
                for(let w of recordData.wins) {
                    for(let player of players) {
                        if (w.id === player.id) {
                            w.name = player.name;   // Update player name in case player changed it during this game
                            // Increment count for winning player
                            if(w.id === winningPlayer.id) {
                                w.wins +=1;
                            }
                        }
                    }
                }

                // Check if new records
                if(recordData.longestWord.word.length < longestWord.length) {
                    recordData.longestWord.word = longestWord;
                    recordData.longestWord.playerName = longestWordPlayer;
                }
                if(recordData.mostWords.count < mostWords) {
                    recordData.mostWords.count = mostWords;
                    recordData.mostWords.playerName = mostWordsPlayer;
                }

                recordData.lastGame = gameId;

                await update(recordRef, recordData);
            }
        }

    } catch (error) {
        console.error("Error processing game: ", error);
    }

            

    newStats['recordStats'] = recordData;
    
    // Calculate all-time stats for individual players
    // TODO

    return newStats;
}

const StatRow = ({ label, value }) => (
    <div className="sg-row" style={{
        justifyContent: 'space-between',
        gap: 12,
        padding: '11px 14px',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface-2)',
        boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.7)',
    }}>
        <span className="sg-muted" style={{ fontWeight: 700 }}>{label}</span>
        <span style={{ fontWeight: 800, textAlign: 'right' }}>{value}</span>
    </div>
);

const GameOver = ({ stats, onGameOverClosed }) => {
    const [open, setOpen] = useState(true);

    const handleClose = () => {
        setOpen(false);
        onGameOverClosed();
    };

    useEffect(() => {
        burst();
    }, []);

    if (!stats) {
        return <div className="sg-muted" style={{ padding: 24, textAlign: 'center' }}>Loading…</div>; // Or any other loading state representation
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="game-over-modal-title"
            aria-describedby="game-over-modal-description"
        >
            <Box sx={style} className="sg-modal-paper sg-anim-pop">
                <div className="sg-center-col" style={{ padding: 0 }}>
                    <p className="sg-kicker sg-anim-fadeup" style={{ textAlign: 'center', margin: '0 0 6px' }}>
                        Game over
                    </p>
                    <h2 id="game-over-modal-title" className="sg-anim-pop" style={{
                        textAlign: 'center',
                        fontSize: '1.9rem',
                        margin: '0 0 18px',
                    }}>
                        🏆 {stats.gameStats.winner.playerName} wins!
                    </h2>

                    {stats.gameStats &&
                        <section style={{ marginBottom: 16 }}>
                            <h3 style={{ fontSize: '1.1rem', margin: '0 0 8px' }}>This game</h3>
                            <div className="sg-stack sg-stagger" style={{ gap: 8 }}>
                                <StatRow
                                    label="Longest word"
                                    value={`${stats.gameStats.longestWord.word} (${stats.gameStats.longestWord.playerName})`}
                                />
                                <StatRow
                                    label="Most words"
                                    value={`${stats.gameStats.mostWords.count} (${stats.gameStats.mostWords.playerName})`}
                                />
                            </div>
                        </section>
                    }

                    {stats.recordStats && stats.recordStats.gameCount > 1 &&
                        <section style={{ marginBottom: 16 }}>
                            <h3 style={{ fontSize: '1.1rem', margin: '0 0 8px' }}>All-time</h3>
                            <div className="sg-stack sg-stagger" style={{ gap: 8 }}>
                                {stats.recordStats.wins.map((player) => (
                                    <StatRow
                                        key={player.playerName}
                                        label={`${player.playerName} wins`}
                                        value={player.wins}
                                    />
                                ))}
                                <StatRow
                                    label="All-time longest word"
                                    value={`${stats.recordStats.longestWord.word} (${stats.recordStats.longestWord.playerName})`}
                                />
                                <StatRow
                                    label="All-time most words"
                                    value={`${stats.recordStats.mostWords.count} (${stats.recordStats.mostWords.playerName})`}
                                />
                            </div>
                        </section>
                    }

                    <div className="sg-row" style={{ justifyContent: 'center', marginTop: 20 }}>
                        <button type="button" className="sg-btn sg-btn--primary" onClick={handleClose}>
                            Play again 🎲
                        </button>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

export default GameOver;
